import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function HeroImageUpload({ images, onImagesChange, onSave, isSaving }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [error, setError] = useState('');
  const fileInputRefs = useRef({});

  const handleFileSelect = async (index, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file?.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setUploadingIndex(index);
    setError('');

    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `slideshow/${fileName}`;

      const { error: uploadError } = await supabase?.storage?.from('hero-slideshow-images')?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase?.storage?.from('hero-slideshow-images')?.getPublicUrl(filePath);

      const updatedImages = [...images];
      updatedImages[index] = {
        ...updatedImages?.[index],
        url: data?.publicUrl,
        path: filePath
      };

      onImagesChange(updatedImages);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload image');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleRemoveImage = async (index) => {
    const image = images?.[index];
    
    // Delete from storage if it has a path
    if (image?.path) {
      try {
        await supabase?.storage?.from('hero-slideshow-images')?.remove([image?.path]);
      } catch (err) {
        console.error('Error deleting image:', err);
      }
    }

    const updatedImages = images?.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updatedImages = [...images];
    [updatedImages[index - 1], updatedImages[index]] = [updatedImages?.[index], updatedImages?.[index - 1]];
    onImagesChange(updatedImages);
  };

  const handleMoveDown = (index) => {
    if (index === images?.length - 1) return;
    const updatedImages = [...images];
    [updatedImages[index], updatedImages[index + 1]] = [updatedImages?.[index + 1], updatedImages?.[index]];
    onImagesChange(updatedImages);
  };

  const handleAddImage = () => {
    const newImage = {
      url: '',
      alt: '',
      caption: '',
      path: ''
    };
    onImagesChange([...images, newImage]);
  };

  const handleUpdateField = (index, field, value) => {
    const updatedImages = [...images];
    updatedImages[index] = {
      ...updatedImages?.[index],
      [field]: value
    };
    onImagesChange(updatedImages);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-red-800" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}
      {images?.map((image, index) => (
        <div key={index} className="bg-white bg-opacity-10 rounded-xl p-4 border border-white border-opacity-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <h4 className="font-semibold text-white">Image {index + 1}</h4>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="p-1 text-blue-300 hover:text-blue-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <Icon name="ChevronUp" size={20} />
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === images?.length - 1}
                  className="p-1 text-blue-300 hover:text-blue-100 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <Icon name="ChevronDown" size={20} />
                </button>
              </div>
            </div>
            <button
              onClick={() => handleRemoveImage(index)}
              className="text-red-400 hover:text-red-300"
            >
              <Icon name="Trash2" size={20} />
            </button>
          </div>

          <div className="space-y-3">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Upload Image
              </label>
              <input
                type="file"
                ref={(el) => (fileInputRefs.current[index] = el)}
                onChange={(e) => handleFileSelect(index, e)}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRefs?.current?.[index]?.click()}
                disabled={uploadingIndex === index}
                iconName={uploadingIndex === index ? 'Loader' : 'Upload'}
                iconPosition="left"
                className="w-full border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
              >
                {uploadingIndex === index ? 'Uploading...' : 'Choose Image'}
              </Button>
            </div>

            {/* Alt Text */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Alt Text (for accessibility)
              </label>
              <input
                type="text"
                value={image?.alt || ''}
                onChange={(e) => handleUpdateField(index, 'alt', e?.target?.value)}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Descriptive text for screen readers"
              />
            </div>

            {/* Caption */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Caption (optional)
              </label>
              <input
                type="text"
                value={image?.caption || ''}
                onChange={(e) => handleUpdateField(index, 'caption', e?.target?.value)}
                className="w-full px-4 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                placeholder="Optional caption text"
              />
            </div>

            {/* Image Preview */}
            {image?.url && (
              <div className="mt-3">
                <img
                  src={image?.url}
                  alt={image?.alt || 'Preview'}
                  className="w-full h-40 object-cover rounded-xl border border-white border-opacity-20"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={handleAddImage}
          iconName="Plus"
          iconPosition="left"
          className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl"
        >
          Add Image
        </Button>

        <Button
          onClick={onSave}
          disabled={isSaving}
          iconName="Save"
          iconPosition="left"
          className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl"
        >
          {isSaving ? 'Saving...' : 'Save Slideshow'}
        </Button>
      </div>
      <p className="text-sm text-blue-100 flex items-start">
        <Icon name="Info" size={16} className="mr-2 flex-shrink-0 mt-0.5" />
        Images will automatically rotate every 5 seconds on the hero section. Use high-resolution images (1920x1080 or larger) for best results.
      </p>
    </div>
  );
}